import { CurrencyDollarIcon } from "@heroicons/react/20/solid";
import { useEffect, useRef } from "react";
import { OtsGoBot } from "./OtsGoBot";

export type OtsGoBotPopOutProps = {
  /** When the pop-out is over, to take it away; also called when motion is reduced and nothing plays. */
  onDone?: () => void;
  size?: number;
  /** Which way he pops out of the point: up out of a button, or down below a cell; up by default. */
  direction?: "up" | "down";
  className?: string;
};

/** How long the pop-out plays, toss included. */
export const GOBOT_POP_OUT_MS = 1_700;

// GoBot pops out from the point (up, or down below it), hops, and flips a coin off his hat, then goes back in.
// Reduced motion shows nothing.
const popOutCss = [
  "@keyframes ots-gobot-pop{0%{transform:translate(-50%,40%) scale(.2);opacity:0}16%{transform:translate(-50%,-78%) scale(1.12);opacity:1}26%{transform:translate(-50%,-70%) scale(1)}82%{transform:translate(-50%,-70%) scale(1);opacity:1}100%{transform:translate(-50%,30%) scale(.3);opacity:0}}",
  "@keyframes ots-gobot-toss{0%{transform:translate(0,0) rotateY(0);opacity:0}8%{opacity:1}55%{transform:translate(15px,-18px) rotateY(540deg);opacity:1}100%{transform:translate(28px,8px) rotateY(1080deg);opacity:0}}",
  "@keyframes ots-gobot-pop-down{0%{transform:translate(-50%,-40%) scale(.2);opacity:0}16%{transform:translate(-50%,78%) scale(1.12);opacity:1}26%{transform:translate(-50%,70%) scale(1)}82%{transform:translate(-50%,70%) scale(1);opacity:1}100%{transform:translate(-50%,-30%) scale(.3);opacity:0}}",
  ".ots-gobot-pop{animation:ots-gobot-pop 1.7s cubic-bezier(.3,.7,.4,1) both}",
  ".ots-gobot-pop-down{animation-name:ots-gobot-pop-down}",
  ".ots-gobot-toss{animation:ots-gobot-toss .9s cubic-bezier(.3,.6,.5,1) .5s both}",
  "@media (prefers-reduced-motion:reduce){.ots-gobot-pop{display:none}}",
].join("");

/**
 * GoBot celebrating finished work: he pops out of the point he is placed at (this zero-size anchor), up or
 * down, cheers, and tosses a coin. Decorative; say what finished in text nearby.
 */
export const OtsGoBotPopOut = ({ onDone, size = 26, direction = "up", className }: OtsGoBotPopOutProps) => {
  // Timed from mount, so a host redrawing for another pop-out does not restart this one.
  const done = useRef(onDone);
  done.current = onDone;
  useEffect(() => {
    const timer = window.setTimeout(() => done.current?.(), GOBOT_POP_OUT_MS);
    return () => window.clearTimeout(timer);
  }, []);
  return (
    <span aria-hidden="true" data-gobot-pop-out="" className={["pointer-events-none absolute size-0", className].filter(Boolean).join(" ")}>
      <style>{popOutCss}</style>
      <span data-direction={direction} className={[
        "ots-gobot-pop absolute left-0 block text-[var(--ots-primary)]",
        direction === "down" ? "ots-gobot-pop-down top-0" : "bottom-0",
      ].join(" ")}>
        <OtsGoBot state="cheer" size={size}/>
        {/* The costing button's own coin, in gold. */}
        <CurrencyDollarIcon data-gobot-coin="" className="ots-gobot-toss absolute left-1/2 top-0 size-3.5 text-amber-400 drop-shadow-sm"/>
      </span>
    </span>
  );
};
